// frontend-react/src/components/RuleManagement/LegaltechRuleEditModal.tsx
import React, { useEffect, useState } from 'react';
import { Button, Collapse, Divider, Form, Input, Modal, Select, Tag, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { LegaltechRule, LegaltechRuleCreate, LegaltechRuleUpdate, RiskLevel } from '../../types/legaltechRule';
import { createLegaltechRule, updateLegaltechRule, normalizeRule } from '../../services/legaltechRuleService';

const { Text } = Typography;
const { TextArea } = Input;

/** 律師系統的 11 個適用標籤 */
const LAWYER_TAGS = [
  '保密協議通則',
  '保密協議（接收方）',
  '保密協議（揭露方）',
  '僱傭合約（部分工時）',
  '僱傭合約（全職）',
  '商標授權契約',
  '國內旅遊定型化契約',
  '國外旅遊定型化契約',
  '住宅租賃定型化契約',
  '簡易型一日遊國內旅遊定型化契約',
  '海外旅遊學習定型化契約',
];

interface Props {
  open: boolean;
  rule: LegaltechRule | null; // null = 新增模式
  availableTags: string[];
  onClose: () => void;
  onSaved: (rule: LegaltechRule) => void;
}

const RISK_LEVEL_OPTIONS = [
  { label: '高', value: 'high' as RiskLevel },
  { label: '中', value: 'medium' as RiskLevel },
  { label: '低', value: 'low' as RiskLevel },
];

const LegaltechRuleEditModal: React.FC<Props> = ({ open, rule, availableTags, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const isEdit = rule !== null;
  const [normalizing, setNormalizing] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    if (open) {
      if (rule) {
        form.setFieldsValue({
          issue_tags: rule.issue_tags || [],
          name: rule.name,
          content: rule.content,
          contract_type: rule.contract_type ?? '',
          examples: rule.examples ?? '',
          ai_content: rule.ai_content ?? '',
          // 結構化欄位
          selection_mode: rule.selection_mode || 'auto',
          trigger_keywords: rule.trigger_keywords || [],
          trigger_condition: rule.trigger_condition ?? '',
          pass_criteria: rule.pass_criteria ?? '',
          fail_criteria: rule.fail_criteria ?? '',
          exception_condition: rule.exception_condition ?? '',
          output_suggestion: rule.output_suggestion ?? '',
          risk_level: rule.risk_level ?? undefined,
          rule_type: rule.rule_type ?? '',
          target_clause_type: rule.target_clause_type ?? '',
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ selection_mode: 'auto', trigger_keywords: [], issue_tags: [] });
      }
      setKeywordInput('');
    }
  }, [open, rule, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const issueTags: string[] = values.issue_tags || [];
      const payload = {
        category: issueTags[0] || '未分類', // 向後相容：取第一個標籤作為 category
        name: values.name?.trim(),
        content: values.content?.trim(),
        ai_content: values.ai_content?.trim() || undefined,
        contract_type: values.contract_type?.trim() || undefined,
        examples: values.examples?.trim() || undefined,
        // 結構化欄位
        issue_tags: issueTags.length ? issueTags : undefined,
        selection_mode: values.selection_mode || 'auto',
        trigger_keywords: values.trigger_keywords?.length ? values.trigger_keywords : undefined,
        trigger_condition: values.trigger_condition?.trim() || undefined,
        pass_criteria: values.pass_criteria?.trim() || undefined,
        fail_criteria: values.fail_criteria?.trim() || undefined,
        exception_condition: values.exception_condition?.trim() || undefined,
        output_suggestion: values.output_suggestion?.trim() || undefined,
        risk_level: values.risk_level || undefined,
        rule_type: values.rule_type?.trim() || undefined,
        target_clause_type: values.target_clause_type?.trim() || undefined,
      };

      let saved: LegaltechRule;
      if (isEdit) {
        saved = await updateLegaltechRule(rule!.id, payload as LegaltechRuleUpdate);
        message.success('規則已更新');
      } else {
        saved = await createLegaltechRule(payload as LegaltechRuleCreate);
        message.success('規則已新增');
      }
      onSaved(saved);
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.message ?? '操作失敗');
    }
  };

  const handleNormalize = async () => {
    if (!isEdit || !rule) return;
    setNormalizing(true);
    try {
      const result = await normalizeRule(rule.id);
      console.log('[律果規則] AI 分析回傳:', result);
      if (result.status === 'success') {
        if (result.rule) {
          // 直接回填所有 AI 生成的欄位到表單
          const r = result.rule;
          form.setFieldsValue({
            ai_content: r.ai_content ?? '',
            examples: r.examples ?? '',
            trigger_keywords: r.trigger_keywords || [],
            trigger_condition: r.trigger_condition ?? '',
            pass_criteria: r.pass_criteria ?? '',
            fail_criteria: r.fail_criteria ?? '',
            exception_condition: r.exception_condition ?? '',
            output_suggestion: r.output_suggestion ?? '',
            risk_level: r.risk_level ?? undefined,
            rule_type: r.rule_type ?? '',
            target_clause_type: r.target_clause_type ?? '',
            selection_mode: r.selection_mode || 'auto',
          });
          message.success('AI 分析完成');
        } else {
          message.success('AI 分析完成，請關閉後重新開啟查看結果');
        }
      } else {
        message.warning(result.message);
      }
    } catch (err: any) {
      message.error(err?.message ?? 'AI 分析失敗');
    } finally {
      setNormalizing(false);
    }
  };

  const handleAddKeyword = () => {
    const kw = keywordInput.trim();
    if (!kw) return;
    const current: string[] = form.getFieldValue('trigger_keywords') || [];
    if (!current.includes(kw)) {
      form.setFieldsValue({ trigger_keywords: [...current, kw] });
    }
    setKeywordInput('');
  };

  const handleRemoveKeyword = (keyword: string) => {
    const current: string[] = form.getFieldValue('trigger_keywords') || [];
    form.setFieldsValue({ trigger_keywords: current.filter(k => k !== keyword) });
  };

  // 合併預設標籤 + 資料庫中已有的標籤（去重）
  const allTagValues = Array.from(new Set([...LAWYER_TAGS, ...availableTags]));
  const tagOptions = allTagValues.map(t => ({ label: t, value: t }));

  return (
    <Modal
      title={isEdit ? '編輯律果規則' : '新增律果規則'}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText={isEdit ? '儲存' : '新增'}
      cancelText='取消'
      width={860}
      destroyOnClose
    >
      <Form form={form} layout='vertical' requiredMark='optional'>
        {/* ── 律師輸入區 ── */}
        <Form.Item
          name='name'
          label='規則名稱'
          rules={[{ required: true, message: '請輸入規則名稱' }]}
        >
          <Input placeholder='如：接收方保密義務' />
        </Form.Item>

        <Form.Item
          name='content'
          label='規則內容'
          rules={[{ required: true, message: '請輸入規則內容' }]}
        >
          <TextArea rows={8} placeholder='詳細的合規要求或條款說明...' />
        </Form.Item>

        <div style={{ display: 'flex', gap: 16 }}>
          <Form.Item name='contract_type' label='合約類型' style={{ flex: 1 }}>
            <Select
              allowClear
              placeholder='選擇合約類型'
              options={[
                { label: '定型化契約', value: '定型化契約' },
                { label: '一般合約', value: '一般合約' },
              ]}
            />
          </Form.Item>
          <Form.Item name='risk_level' label='風險等級' style={{ flex: 1 }}>
            <Select allowClear placeholder='選擇風險等級' options={RISK_LEVEL_OPTIONS} />
          </Form.Item>
        </div>

        <Form.Item
          name='issue_tags'
          label='適用標籤'
          rules={[{ required: true, message: '請至少選擇一個適用標籤' }]}
          extra='選擇此規則適用於哪些合約場景（可多選）'
        >
          <Select
            mode='multiple'
            allowClear
            placeholder='選擇適用的合約場景'
            options={tagOptions}
          />
        </Form.Item>

        {/* ── AI 分析按鈕 ── */}
        <Divider style={{ margin: '12px 0' }} />
        {isEdit && (
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <Button
              type='primary'
              icon={<ThunderboltOutlined />}
              loading={normalizing}
              onClick={handleNormalize}
              size='large'
            >
              AI 分析規則
            </Button>
          </div>
        )}

        {/* ── AI 審閱用內容（直接顯示，最重要） ── */}
        <Form.Item
          name='ai_content'
          label='AI 審閱用內容'
          extra='AI 最終拿來審閱合約的完整文字，可手動調整'
        >
          <TextArea
            rows={8}
            placeholder={'AI 分析後將自動生成，格式如下：\n\n1、審閱目的\n接收方對於揭露方所提供之機密資料...\n\n2、觸發條件\n合約中出現保密義務相關條款\n\n3、不通過標準\n未規定注意義務標準，或標準低於善良管理人義務\n\n4、例外情況\n雙方另有書面約定特殊注意義務標準者'}
          />
        </Form.Item>

        {/* ── AI 生成的詳細資訊（摺疊，用於規則篩選參考） ── */}
        <Collapse
          ghost
          items={[{
            key: 'ai-details',
            label: 'AI 生成的詳細資訊（規則篩選參考）',
            children: (
              <>
                <Form.Item name='examples' label='範例'>
                  <ExamplesEditor />
                </Form.Item>

                <Form.Item name='trigger_keywords' label='觸發關鍵字'>
                  <div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <Input
                        placeholder='輸入關鍵字後按 Enter'
                        value={keywordInput}
                        onChange={e => setKeywordInput(e.target.value)}
                        onPressEnter={e => { e.preventDefault(); handleAddKeyword(); }}
                        style={{ flex: 1 }}
                      />
                      <Button onClick={handleAddKeyword}>新增</Button>
                    </div>
                    <div>
                      <Form.Item name='trigger_keywords' noStyle>
                        <KeywordTags onRemove={handleRemoveKeyword} />
                      </Form.Item>
                    </div>
                  </div>
                </Form.Item>

                <Form.Item name='trigger_condition' label='觸發條件'>
                  <TextArea rows={2} placeholder={'合約中出現哪些條款時，應啟用此規則進行檢查。\n例：合約中出現保密義務相關條款，包含機密資料定義、保密期限、或接收方義務之約定'} />
                </Form.Item>

                <Form.Item
                  name='fail_criteria'
                  label='不通過標準'
                  extra='告訴 AI 什麼算違規 — 符合以下任一條件即判定不通過'
                >
                  <TextArea rows={3} placeholder={'例：\n1. 未規定注意義務標準\n2. 注意義務低於善良管理人標準\n3. 保密期限少於三年\n4. 未明確禁止揭露予第三人'} />
                </Form.Item>

                <Form.Item
                  name='pass_criteria'
                  label='通過標準'
                  extra='告訴 AI 什麼算合規 — 符合以下條件即判定通過'
                >
                  <TextArea rows={3} placeholder={'例：\n1. 明確約定善良管理人以上之注意義務\n2. 保密期限至少三年或至機密資料喪失機密性\n3. 明確禁止揭露予第三人'} />
                </Form.Item>

                <Form.Item
                  name='exception_condition'
                  label='例外情況'
                  extra='告訴 AI 即使看起來不通過，但在這些情況下可以放過'
                >
                  <TextArea rows={3} placeholder={'例：\n1. 雙方另有書面約定特殊注意義務標準者\n2. 經揭露方事前書面同意揭露者\n3. 依法院裁判或政府機關命令要求揭露者'} />
                </Form.Item>

                <Form.Item
                  name='output_suggestion'
                  label='建議輸出格式'
                  extra='告訴 AI 發現問題時，應如何撰寫修改建議'
                >
                  <TextArea rows={2} placeholder={'例：建議加入善良管理人注意義務之明確規定，並將保密期限延長至契約終止後至少三年'} />
                </Form.Item>

                <Form.Item name='rule_type' label='規則類型'>
                  <Select
                    allowClear
                    placeholder='如：禁止條款、必備條款'
                    options={[
                      { label: '禁止條款', value: '禁止條款' },
                      { label: '必備條款', value: '必備條款' },
                      { label: '風險提示', value: '風險提示' },
                      { label: '建議條款', value: '建議條款' },
                    ]}
                  />
                </Form.Item>

                <Form.Item name='target_clause_type' label='目標條款類型'>
                  <Input placeholder='如：保密條款、違約金條款' />
                </Form.Item>
              </>
            ),
          }]}
        />
      </Form>
    </Modal>
  );
};

/** 範例多筆編輯元件 */
interface ExampleItem {
  clause: string;
  opinion: string;
}

const parseExamples = (raw?: string): ExampleItem[] => {
  if (!raw) return [];
  // 嘗試解析 JSON 格式
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch { /* fallback */ }
  // fallback: 把整段文字當成一筆範例
  return raw.trim() ? [{ clause: raw.trim(), opinion: '' }] : [];
};

const serializeExamples = (items: ExampleItem[]): string => {
  const filtered = items.filter(e => e.clause.trim() || e.opinion.trim());
  if (!filtered.length) return '';
  return JSON.stringify(filtered);
};

const ExamplesEditor: React.FC<{
  value?: string;
  onChange?: (val: string) => void;
}> = ({ value, onChange }) => {
  const [items, setItems] = useState<ExampleItem[]>(() => {
    const parsed = parseExamples(value);
    return parsed.length ? parsed : [{ clause: '', opinion: '' }];
  });

  useEffect(() => {
    const parsed = parseExamples(value);
    if (parsed.length) setItems(parsed);
  }, [value]);

  const update = (newItems: ExampleItem[]) => {
    setItems(newItems);
    onChange?.(serializeExamples(newItems));
  };

  const handleChange = (index: number, field: 'clause' | 'opinion', val: string) => {
    const next = [...items];
    const cur = next[index] ?? { clause: '', opinion: '' };
    next[index] = { clause: cur.clause, opinion: cur.opinion, [field]: val };
    update(next);
  };

  const handleAdd = () => {
    update([...items, { clause: '', opinion: '' }]);
  };

  const handleRemove = (index: number) => {
    if (items.length <= 1) return;
    update(items.filter((_, i) => i !== index));
  };

  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          padding: '12px 12px 8px',
          marginBottom: 8,
          background: '#fafafa',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <Text strong style={{ fontSize: 12 }}>範例 {i + 1}</Text>
            {items.length > 1 && (
              <Button
                type='text'
                size='small'
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemove(i)}
              />
            )}
          </div>
          <TextArea
            rows={2}
            placeholder='條款內容：當合約中出現什麼樣的條款...'
            value={item.clause}
            onChange={e => handleChange(i, 'clause', e.target.value)}
            style={{ marginBottom: 6 }}
          />
          <TextArea
            rows={2}
            placeholder='審閱意見：律師會給出什麼樣的審閱意見...'
            value={item.opinion}
            onChange={e => handleChange(i, 'opinion', e.target.value)}
          />
        </div>
      ))}
      <Button
        type='dashed'
        icon={<PlusOutlined />}
        onClick={handleAdd}
        style={{ width: '100%' }}
      >
        新增範例
      </Button>
    </div>
  );
};

/** 關鍵字 Tag 顯示元件 */
const KeywordTags: React.FC<{
  value?: string[];
  onRemove: (keyword: string) => void;
}> = ({ value = [], onRemove }) => {
  if (!value.length) return <span style={{ color: '#999', fontSize: 12 }}>尚未設定關鍵字</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {value.map(kw => (
        <Tag key={kw} closable onClose={() => onRemove(kw)} color='blue'>
          {kw}
        </Tag>
      ))}
    </div>
  );
};

export default LegaltechRuleEditModal;
